import { useEffect, useMemo, useState } from 'react'

import { ClubSelector } from '@/components/entries/ClubSelector'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useCheckInStepperContext } from '../CheckInStepper'

type ClubSelectionState = {
  clubId: string | null
  clubName: string
}

type ClubStepProps = {
  clubSelection: ClubSelectionState
  onClubSelectionChange: (next: Partial<ClubSelectionState>) => void
  isOnline: boolean
  showHasOrganization: boolean
  showOrganizationId: string | null
  grandChampionEligible: boolean
  onGrandChampionEligibilityChange: (next: boolean, options?: { exhibitorId?: string }) => void
  disableGrandChampionToggle?: boolean
  grandChampionToggleTitle?: string
  onContinue?: () => Promise<boolean | void> | boolean | void
  continuePending?: boolean
}

export function ClubStep({
  clubSelection,
  onClubSelectionChange,
  isOnline,
  showHasOrganization,
  showOrganizationId,
  grandChampionEligible,
  onGrandChampionEligibilityChange,
  disableGrandChampionToggle = false,
  grandChampionToggleTitle,
  onContinue,
  continuePending,
}: ClubStepProps) {
  const stepper = useCheckInStepperContext()
  const [localPending, setLocalPending] = useState(false)
  const canSearchClubs = isOnline && showHasOrganization
  const [clubEntryMode, setClubEntryMode] = useState<'search' | 'manual'>(
    canSearchClubs ? 'search' : 'manual'
  )
  useEffect(() => {
    if (!canSearchClubs && clubEntryMode !== 'manual') {
      setClubEntryMode('manual')
    }
  }, [canSearchClubs, clubEntryMode])
  const entrantName = useMemo(() => {
    const entrant = stepper.selectedEntrant
    return entrant?.name ?? entrant?.person?.name ?? 'selected entrant'
  }, [stepper.selectedEntrant])
  const isContinuePending = continuePending ?? localPending

  const handleContinue = async () => {
    if (!onContinue) {
      stepper.goToNext()
      return
    }

    let outcome: boolean | void | Promise<boolean | void>
    try {
      outcome = onContinue()
    } catch {
      return
    }

    if (outcome && typeof (outcome as Promise<boolean | void>).then === 'function') {
      setLocalPending(true)
      try {
        const resolved = await outcome
        if (resolved === false) {
          return
        }
      } catch {
        return
      } finally {
        setLocalPending(false)
      }
      stepper.goToNext()
      return
    }

    if (outcome === false) {
      return
    }

    stepper.goToNext()
  }

  return (
    <Card data-testid="club-step-card">
      <CardHeader>
        <CardTitle>Confirm Club</CardTitle>
        <CardDescription>
          Choose the club for {entrantName}. This selection applies to new entries created during
          this check-in session.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs
          value={clubEntryMode}
          onValueChange={value => {
            const nextMode = value as 'search' | 'manual'
            setClubEntryMode(nextMode)
            if (nextMode === 'manual' && clubSelection.clubId) {
              onClubSelectionChange({ clubId: null })
            }
          }}
        >
          <TabsList>
            <TabsTrigger value="search" disabled={!canSearchClubs}>
              Find Club
            </TabsTrigger>
            <TabsTrigger value="manual">Enter Manually</TabsTrigger>
          </TabsList>
          <TabsContent value="search" className="mt-4">
            <ClubSelector
              organizationId={showOrganizationId}
              hasOrganization={showHasOrganization}
              isOnline={isOnline}
              value={clubSelection.clubName}
              onValueChange={value => {
                onClubSelectionChange({ clubName: value })
              }}
              selectedClubId={clubSelection.clubId}
              onClubIdChange={nextId => {
                onClubSelectionChange({ clubId: nextId })
              }}
            />
          </TabsContent>
          <TabsContent value="manual" className="mt-4">
            <div className="space-y-2">
              <Input
                value={clubSelection.clubName}
                onChange={event =>
                  onClubSelectionChange({ clubName: event.target.value, clubId: null })
                }
                placeholder="Enter club name"
              />
              <p className="text-xs text-muted-foreground">
                We’ll store this name as typed if it does not match an existing club.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        <label className="flex items-start justify-between gap-3 rounded-md border border-dashed border-border/60 px-3 py-2">
          <div>
            <p className="text-sm font-medium">Not eligible for Grand Champion</p>
            <p className="text-xs text-muted-foreground">
              When checked, this entrant is excluded from Grand Champion awards and reports.
            </p>
          </div>
          <input
            type="checkbox"
            className="h-4 w-4 rounded border border-input"
            checked={!grandChampionEligible}
            onChange={event => onGrandChampionEligibilityChange(!event.target.checked)}
            disabled={disableGrandChampionToggle}
            aria-label="Not eligible for Grand Champion"
            title={grandChampionToggleTitle}
            data-testid="grand-champion-ineligible-toggle"
          />
        </label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button type="button" variant="outline" onClick={() => stepper.goToPrevious()}>
            Back to Entrant
          </Button>
          <div className="flex gap-2 sm:justify-end">
            <Button
              type="button"
              onClick={handleContinue}
              disabled={isContinuePending}
              aria-busy={isContinuePending}
            >
              Continue to Entries
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
